import {
  doc,
  collection,
  getDoc,
  updateDoc,
  increment,
  addDoc,
  onSnapshot,
  Unsubscribe,
  Timestamp,
  Transaction,
  getDocs,
  setDoc,
  runTransaction,
  query,
  where,
} from "firebase/firestore";
import * as Sentry from "@sentry/react"; // Import Sentry for error tracking
import { db } from "../config/firebase";

/**
 * Creates a new wallet for an organization, team, or user.
 * @param type The type of the wallet ('organization', 'team', 'user')
 * @param entityId The ID of the entity (organization, team, or user) the wallet belongs to
 */
export const createWallet = async (
  type: "organization" | "team" | "user",
  entityId: string
) => {
  try {
    console.log("Creating wallet with params:", { type, entityId });

    if (!entityId) {
      throw new Error("Entity ID is required");
    }

    const walletRef = doc(db, "wallets", entityId); // Use entityId as wallet ID
    const walletDoc = await getDoc(walletRef);

    if (walletDoc.exists()) {
      console.log("Wallet already exists for:", entityId);
      throw new Error("Wallet already exists");
    }

    const walletData = {
      id: entityId,
      type,
      entityId,
      balance: 0,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    await setDoc(walletRef, walletData);
    console.log("Wallet created successfully:", walletData);
    return { success: true, wallet: walletData };
  } catch (error: any) {
    console.error("Error in createWallet:", error);
    Sentry.captureException(error, {
      tags: { operation: "createWallet", type },
      extra: { entityId },
    });
    console.error("Error creating wallet:", error);
    return { success: false, error: error.message || "Unknown error" };
  }
};

/**
 * Retrieves a wallet and its transactions.
 * @param walletId The ID of the wallet to fetch
 */
export const getWallet = async (walletId: string) => {
  try {
    const walletRef = doc(db, "wallets", walletId);
    const walletDoc = await getDoc(walletRef);

    if (!walletDoc.exists()) {
      throw new Error("Wallet not found");
    }

    const walletData = walletDoc.data();

    // Fetch transactions
    const transactionsRef = collection(db, `wallets/${walletId}/transactions`);
    const transactionsSnap = await getDocs(transactionsRef);
    const transactions = transactionsSnap.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Calculate total earnings (sum of all credit transactions)
    let totalEarnings = 0;
    transactions.forEach((transaction: any) => {
      if (transaction.type === "credit") {
        totalEarnings += Number(transaction.amount);
      }
    });
    walletData.totalEarnings = totalEarnings;

    // Fetch additional user data that might include earnings info
    try {
      // The walletId is the userId for user wallets
      const userDoc = await getDoc(doc(db, "users", walletId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.totalEarnings && !walletData.totalEarnings) {
          walletData.totalEarnings = userData.totalEarnings;
        }
        // Get completed sessions count
        if (userData.completedSessions) {
          walletData.completedSessions = userData.completedSessions;
        }
      }

      // Also check activities collection to get accurate earnings data
      const activitiesRef = collection(db, "activities");
      const q = query(
        activitiesRef,
        where("expertId", "==", walletId),
        where("status", "==", "completed")
      );
      const activitiesSnap = await getDocs(q);

      if (!activitiesSnap.empty) {
        let activityEarnings = 0;
        activitiesSnap.docs.forEach((doc) => {
          const activityData = doc.data();
          if (
            activityData.earnings &&
            typeof activityData.earnings === "number"
          ) {
            activityEarnings += activityData.earnings;
          }
        });

        // Use activity-based earnings if higher than transaction-based earnings
        if (activityEarnings > totalEarnings) {
          walletData.totalEarnings = activityEarnings;
        }
      }
    } catch (error) {
      console.warn("Could not fetch additional earnings data:", error);
    }

    return { success: true, wallet: walletData, transactions };
  } catch (error: any) {
    Sentry.captureException(error, {
      tags: { operation: "getWallet" },
      extra: { walletId },
    });
    console.error("Error fetching wallet:", error);
    return { success: false, error: error.message || "Unknown error" };
  }
};

/**
 * Adds funds to a wallet.
 * @param walletId The ID of the wallet
 * @param amount The amount to add
 * @param createdById The user ID of the person adding funds
 * @param description Optional description for the transaction
 */
export const addFunds = async (
  walletId: string,
  amount: number,
  createdById: string,
  description = "Funds added"
) => {
  try {
    const walletRef = doc(db, "wallets", walletId);
    const transactionsRef = collection(db, `wallets/${walletId}/transactions`);

    // Update wallet balance atomically
    await updateDoc(walletRef, {
      balance: increment(amount),
      updatedAt: Timestamp.now(),
    });

    // Record the transaction
    const transactionData = {
      type: "credit",
      amount,
      description,
      createdById,
      createdAt: Timestamp.now(),
    };
    await addDoc(transactionsRef, transactionData);

    return { success: true, transaction: transactionData };
  } catch (error: any) {
    Sentry.captureException(error, {
      tags: { operation: "addFunds" },
      extra: { walletId, amount, createdById, description },
    });
    console.error("Error adding funds:", error);
    return { success: false, error: error.message || "Unknown error" };
  }
};

/**
 * Deducts funds from a wallet.
 * @param walletId The ID of the wallet
 * @param amount The amount to deduct
 * @param createdById The user ID of the person deducting funds
 * @param description Optional description for the transaction
 */
export const deductFunds = async (
  walletId: string,
  amount: number,
  createdById: string,
  description = "Funds deducted"
) => {
  try {
    const walletRef = doc(db, "wallets", walletId);
    const walletDoc = await getDoc(walletRef);

    if (!walletDoc.exists()) {
      throw new Error("Wallet not found");
    }

    const walletData = walletDoc.data();
    if (walletData.balance < amount) {
      throw new Error("Insufficient balance");
    }

    const transactionsRef = collection(db, `wallets/${walletId}/transactions`);

    // Update wallet balance atomically
    await updateDoc(walletRef, {
      balance: increment(-amount),
      updatedAt: Timestamp.now(),
    });

    // Record the transaction
    const transactionData = {
      type: "debit",
      amount,
      description,
      createdById,
      createdAt: Timestamp.now(),
    };
    await addDoc(transactionsRef, transactionData);

    return { success: true, transaction: transactionData };
  } catch (error: any) {
    Sentry.captureException(error, {
      tags: { operation: "deductFunds" },
      extra: { walletId, amount, createdById, description },
    });
    console.error("Error deducting funds:", error);
    return { success: false, error: error.message || "Unknown error" };
  }
};

/**
 * Transfers funds between two wallets.
 * @param fromWalletId The ID of the source wallet
 * @param toWalletId The ID of the destination wallet
 * @param amount The amount to transfer
 * @param createdById The user ID of the person initiating the transfer
 */
export const transferFunds = async (
  fromWalletId: string,
  toWalletId: string,
  amount: number,
  createdById: string
) => {
  try {
    const fromWalletRef = doc(db, "wallets", fromWalletId);
    const toWalletRef = doc(db, "wallets", toWalletId);

    // Perform the transaction atomically using the correct Firebase v9 transaction API
    await runTransaction(db, async (transaction: Transaction) => {
      const fromWalletDoc = await transaction.get(fromWalletRef);
      const toWalletDoc = await transaction.get(toWalletRef);

      if (!fromWalletDoc.exists()) {
        throw new Error("Source wallet not found");
      }

      if (!toWalletDoc.exists()) {
        throw new Error("Destination wallet not found");
      }

      const fromWalletData = fromWalletDoc.data();
      if (fromWalletData.balance < amount) {
        throw new Error("Insufficient balance in source wallet");
      }

      // Deduct from source wallet
      transaction.update(fromWalletRef, {
        balance: increment(-amount),
        updatedAt: Timestamp.now(),
      });

      // Add to destination wallet
      transaction.update(toWalletRef, {
        balance: increment(amount),
        updatedAt: Timestamp.now(),
      });
    });

    // Record transactions in both wallets after the transaction succeeds
    const fromTransactionRef = collection(
      db,
      `wallets/${fromWalletId}/transactions`
    );
    const toTransactionRef = collection(
      db,
      `wallets/${toWalletId}/transactions`
    );

    const fromTransactionData = {
      type: "debit",
      amount,
      description: `Transferred to wallet ${toWalletId}`,
      createdById,
      createdAt: Timestamp.now(),
    };

    const toTransactionData = {
      type: "credit",
      amount,
      description: `Received from wallet ${fromWalletId}`,
      createdById,
      createdAt: Timestamp.now(),
    };

    await addDoc(fromTransactionRef, fromTransactionData);
    await addDoc(toTransactionRef, toTransactionData);

    return { success: true };
  } catch (error: any) {
    Sentry.captureException(error, {
      tags: { operation: "transferFunds" },
      extra: { fromWalletId, toWalletId, amount, createdById },
    });
    console.error("Error transferring funds:", error);
    return { success: false, error: error.message || "Unknown error" };
  }
};

/**
 * Subscribes to wallet updates (real-time listener).
 * @param walletId The ID of the wallet to subscribe to
 * @param onUpdate Callback function to handle wallet updates
 * @returns Unsubscribe function to stop listening
 */
export const subscribeToWallet = (
  walletId: string,
  onUpdate: (wallet: any) => void
): Unsubscribe => {
  if (!walletId) {
    Sentry.captureMessage("Wallet ID is required for subscription", {
      level: "warning",
      extra: { walletId },
    });
    return () => {};
  }
  const walletRef = doc(db, "wallets", walletId);

  const unsubscribe = onSnapshot(walletRef, (docSnapshot) => {
    if (docSnapshot.exists()) {
      onUpdate(docSnapshot.data());
    } else {
      console.warn(`Wallet with ID ${walletId} not found`);
      Sentry.captureMessage(`Wallet with ID ${walletId} not found`, {
        level: "warning",
        extra: { walletId },
      });
      onUpdate(null);
    }
  });

  return unsubscribe;
};

/**
 * Subscribes to wallet transaction updates (real-time listener).
 * @param walletId The ID of the wallet to subscribe to
 * @param onUpdate Callback function to handle transaction updates
 * @returns Unsubscribe function to stop listening
 */
export const subscribeToTransactions = (
  walletId: string,
  onUpdate: (transactions: any[]) => void
): Unsubscribe => {
  const transactionsRef = collection(db, `wallets/${walletId}/transactions`);

  const unsubscribe = onSnapshot(transactionsRef, (querySnapshot) => {
    const transactions = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    onUpdate(transactions);
  });

  return unsubscribe;
};

/**
 * Checks if a wallet exists for a given entity ID
 * @param entityId The ID of the entity to check
 * @returns Promise with wallet existence status and data
 */
export const checkWalletExists = async (entityId: string) => {
  try {
    if (!entityId) {
      throw new Error("Entity ID is required");
    }

    const walletRef = doc(db, "wallets", entityId);
    const walletDoc = await getDoc(walletRef);

    if (walletDoc.exists()) {
      return {
        exists: true,
        wallet: { id: walletDoc.id, ...walletDoc.data() },
      };
    } else {
      return { exists: false, wallet: null };
    }
  } catch (error: any) {
    console.error("Error checking wallet existence:", error);
    return { exists: false, wallet: null, error: error.message };
  }
};

/**
 * Gets or creates a wallet for a given entity
 * @param type The type of the wallet
 * @param entityId The ID of the entity
 * @returns Promise with wallet data
 */
export const getOrCreateWallet = async (
  type: "organization" | "team" | "user",
  entityId: string
) => {
  try {
    // First check if wallet exists
    const existingWallet = await checkWalletExists(entityId);

    if (existingWallet.exists && existingWallet.wallet) {
      console.log("Wallet already exists for:", entityId);
      return { success: true, wallet: existingWallet.wallet, existed: true };
    }

    // If not, create new wallet
    const newWallet = await createWallet(type, entityId);
    return newWallet;
  } catch (error: any) {
    console.error("Error getting or creating wallet:", error);
    return { success: false, error: error.message || "Unknown error" };
  }
};
